/**
 * useProductBarcode Hook - Manages products and stock for barcode page
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { getProductsCliente, Product } from '../services/productService';
import jobService from '../services/jobService';
import { getCurrentUser } from '../services/authService';
import { ItemContagemImportado } from '../services/contagemService';
import { FilterOption } from '../components/MultiSelectFilter';
import { formatDateBR } from '../utils/formatters';

export interface StockItem {
  product: Product;
  lote?: string;
  data_validade?: string;
  numero_serie?: string;
  quantidade?: number;
  tipo_codigo: 'DataMatrix' | 'GS1-128' | 'EAN-13';
  codigo_completo: string;
  job_id?: number;
  job_titulo?: string;
}

interface UseProductBarcodeReturn {
  // Data
  products: Product[];
  stockItems: StockItem[];
  loading: boolean;
  isAdmin: boolean;
  // Filters
  filteredProducts: Product[];
  filteredStockItems: StockItem[];
  clienteOptions: FilterOption[];
  fabricanteOptions: FilterOption[];
  categoriaOptions: FilterOption[];
  selectedClientes: (string | number)[];
  setSelectedClientes: (values: (string | number)[]) => void;
  selectedFabricantes: (string | number)[];
  setSelectedFabricantes: (values: (string | number)[]) => void;
  selectedCategorias: (string | number)[];
  setSelectedCategorias: (values: (string | number)[]) => void;
  searchNome: string;
  setSearchNome: (term: string) => void;
  searchCodigo: string;
  setSearchCodigo: (term: string) => void;
  // Helpers
  formatPrice: (price: number) => string;
  formatDate: (dateString: string) => string;
}

export function useProductBarcode(): UseProductBarcodeReturn {
  const [products, setProducts] = useState<Product[]>([]);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Filters
  const [selectedClientes, setSelectedClientes] = useState<(string | number)[]>([]);
  const [selectedFabricantes, setSelectedFabricantes] = useState<(string | number)[]>([]);
  const [selectedCategorias, setSelectedCategorias] = useState<(string | number)[]>([]);
  const [searchNome, setSearchNome] = useState('');
  const [searchCodigo, setSearchCodigo] = useState('');

  // Check user role
  const checkUserRole = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      setIsAdmin(user?.role === 'ADMIN');
    } catch (error) {
      console.error('Erro ao verificar role do usuário:', error);
      setIsAdmin(false);
    }
  }, []);

  // Load products from client catalog (ProductoCliente table)
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getProductsCliente();
      setProducts(data);
      return data;
    } catch (error) {
      console.error('Erro ao carregar produtos do cliente:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // GTIN-S-002: generateValidGTIN REMOVIDO - PROIBIDO gerar GTINs falsos
  // Se produto não tem GTIN, usar string vazia

  // Verifica se GTIN é genérico (gerado internamente pelo comando gerar_gtin_faltantes)
  const isGenericGTIN = useCallback((gtin: string | undefined): boolean => {
    if (!gtin) return true;
    // GTINs gerados pelo comando gerar_gtin_faltantes começam com 789000
    return gtin.startsWith('789000');
  }, []);

  // GTIN-S-004: Obtém o primeiro GTIN válido do produto (prioridade: gtin_principal > ean > gtins[])
  const getValidGTIN = useCallback((product: Product): string => {
    // 1. Tentar gtin_principal (se não for genérico)
    if (product.gtin && !isGenericGTIN(product.gtin)) {
      return product.gtin;
    }
    // 2. Tentar ean (se não for genérico)
    if (product.ean && !isGenericGTIN(product.ean)) {
      return product.ean;
    }
    // 3. Tentar primeiro GTIN da tabela ProductoClienteGTIN
    if (product.gtins && product.gtins.length > 0) {
      const firstValidGTIN = product.gtins.find(g => g.gtin && !isGenericGTIN(g.gtin));
      if (firstValidGTIN) {
        return firstValidGTIN.gtin;
      }
    }
    // 4. Sem GTIN válido
    return '';
  }, [isGenericGTIN]);

  // Format date to YYMMDD (GS1 format)
  const formatDateToYYMMDD = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    const yy = date.getFullYear().toString().slice(2);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return yy + mm + dd;
  }, []);

  // Load stock from jobs
  const loadStockFromJobs = useCallback(async (productsData: Product[]) => {
    try {
      const response = await jobService.getJobs({ status: 'COMPLETED,IN_PROGRESS' });
      const allStockItems: StockItem[] = [];

      for (const job of response.items) {
        try {
          const counts = await jobService.getCounts(job.id);

          if (counts && Array.isArray(counts)) {
            counts.forEach((item: ItemContagemImportado) => {
              const product = productsData.find(p =>
                p.codigo === item.codigo_produto ||
                p.gtin === item.gtin ||
                p.ean === item.gtin ||
                p.codigo === item.codigo_cliente_produto
              );

              if (product) {
                let tipoCodeigo: 'DataMatrix' | 'GS1-128' | 'EAN-13' = 'EAN-13';
                // GTIN-S-004: Usar getValidGTIN para buscar em gtin_principal, ean E gtins[]
                let codigoCompleto = getValidGTIN(product);

                // Para GS1-128, preferir GTIN do item se válido, senão usar do produto
                const gtinToUse = (item.gtin && !isGenericGTIN(item.gtin))
                  ? item.gtin
                  : getValidGTIN(product);

                if (item.lote && item.data_validade && gtinToUse) {
                  const yymmdd = formatDateToYYMMDD(item.data_validade);
                  codigoCompleto = `(01)${gtinToUse}(17)${yymmdd}(10)${item.lote}`;
                  tipoCodeigo = 'GS1-128';
                } else if (gtinToUse && !item.lote && !item.data_validade) {
                  codigoCompleto = gtinToUse;
                  tipoCodeigo = 'EAN-13';
                }

                allStockItems.push({
                  product,
                  lote: item.lote || undefined,
                  data_validade: item.data_validade || undefined,
                  quantidade: parseFloat(item.quantidade_sistema) || 0,
                  tipo_codigo: tipoCodeigo,
                  codigo_completo: codigoCompleto,
                  job_id: job.id,
                  job_titulo: job.titulo
                });

                // GTIN-S-004: Usar gtinToUse em vez de item.gtin para incluir GTINs da tabela ProductoClienteGTIN
                if (item.lote && item.data_validade && gtinToUse) {
                  allStockItems.push({
                    product,
                    quantidade: parseFloat(item.quantidade_sistema) || 0,
                    tipo_codigo: 'EAN-13',
                    codigo_completo: gtinToUse,
                    job_id: job.id,
                    job_titulo: `${job.titulo} (GTIN Only)`
                  });

                  const yymmdd = formatDateToYYMMDD(item.data_validade);
                  allStockItems.push({
                    product,
                    lote: item.lote,
                    data_validade: item.data_validade,
                    quantidade: parseFloat(item.quantidade_sistema) || 0,
                    tipo_codigo: 'GS1-128',
                    codigo_completo: `(10)${item.lote}(17)${yymmdd}`,
                    job_id: job.id,
                    job_titulo: `${job.titulo} (Lote+Validade)`
                  });
                }
              }
            });
          }
        } catch (error) {
          console.error(`Erro ao carregar contagens do job ${job.id}:`, error);
        }
      }

      setStockItems(allStockItems);
    } catch (error) {
      console.error('Erro ao carregar estoque dos jobs:', error);
    }
  }, [formatDateToYYMMDD, isGenericGTIN, getValidGTIN]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      checkUserRole();
      const productsData = await loadProducts();
      loadStockFromJobs(productsData);
    };
    init();
  }, [checkUserRole, loadProducts, loadStockFromJobs]);

  // Format helpers
  const formatPrice = useCallback((price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  }, []);

  const formatDate = useCallback((dateString: string) => {
    return formatDateBR(dateString);
  }, []);

  // Filter options
  const clienteOptions: FilterOption[] = useMemo(() =>
    Array.from(
      new Set(
        products
          .filter(p => p.cliente_nome_fantasia || p.cliente_razao_social)
          .map(p => JSON.stringify({ value: p.cliente_id, label: p.cliente_nome_fantasia || p.cliente_razao_social }))
      )
    ).map((str: string) => JSON.parse(str))
  , [products]);

  const fabricanteOptions: FilterOption[] = useMemo(() =>
    Array.from(
      new Set(
        products
          .filter(p => p.fabricante)
          .map(p => JSON.stringify({ value: p.fabricante, label: p.fabricante }))
      )
    ).map((str: string) => JSON.parse(str))
  , [products]);

  const categoriaOptions: FilterOption[] = useMemo(() =>
    Array.from(
      new Set(
        products
          .filter(p => p.categoria)
          .map(p => JSON.stringify({ value: p.categoria, label: p.categoria }))
      )
    ).map((str: string) => JSON.parse(str))
  , [products]);

  // Filtered products
  const filteredProducts = useMemo(() =>
    products.filter(product => {
      // FIX: Normalizar tipos para evitar mismatch string/number
      if (selectedClientes.length > 0) {
        const clienteIds = selectedClientes.map(Number);
        if (!product.cliente_id || !clienteIds.includes(product.cliente_id)) return false;
      }
      if (selectedFabricantes.length > 0 && product.fabricante && !selectedFabricantes.includes(product.fabricante)) return false;
      if (selectedCategorias.length > 0 && product.categoria && !selectedCategorias.includes(product.categoria)) return false;
      if (searchNome && !product.nome.toLowerCase().includes(searchNome.toLowerCase())) return false;
      if (searchCodigo && !product.codigo.toLowerCase().includes(searchCodigo.toLowerCase())) return false;
      return true;
    })
  , [products, selectedClientes, selectedFabricantes, selectedCategorias, searchNome, searchCodigo]);

  // Filtered stock items
  const filteredStockItems = useMemo(() =>
    stockItems.filter(item => {
      const product = item.product;
      // FIX: Normalizar tipos para evitar mismatch string/number
      if (selectedClientes.length > 0) {
        const clienteIds = selectedClientes.map(Number);
        if (!product.cliente_id || !clienteIds.includes(product.cliente_id)) return false;
      }
      if (selectedFabricantes.length > 0 && product.fabricante && !selectedFabricantes.includes(product.fabricante)) return false;
      if (selectedCategorias.length > 0 && product.categoria && !selectedCategorias.includes(product.categoria)) return false;
      if (searchNome && !product.nome.toLowerCase().includes(searchNome.toLowerCase())) return false;
      if (searchCodigo && !product.codigo.toLowerCase().includes(searchCodigo.toLowerCase())) return false;
      return true;
    })
  , [stockItems, selectedClientes, selectedFabricantes, selectedCategorias, searchNome, searchCodigo]);

  return {
    products,
    stockItems,
    loading,
    isAdmin,
    filteredProducts,
    filteredStockItems,
    clienteOptions,
    fabricanteOptions,
    categoriaOptions,
    selectedClientes,
    setSelectedClientes,
    selectedFabricantes,
    setSelectedFabricantes,
    selectedCategorias,
    setSelectedCategorias,
    searchNome,
    setSearchNome,
    searchCodigo,
    setSearchCodigo,
    formatPrice,
    formatDate,
  };
}
